// src/hooks/useParentPhotos.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { generateFileFingerprint } from '../util';

const UPLOAD_CONCURRENCY = 3;

export function isLocalPhotoId(id) {
  return typeof id === 'string' && id.startsWith('local-');
}

export function isRealBackendPhotoId(id) {
  return Boolean(id) && !isLocalPhotoId(id);
}

export function isLocalUploadPhoto(photo) {
  return Boolean(
    photo?.kind === 'upload' ||
      photo?.isLocal ||
      photo?.isLocalUpload ||
      isLocalPhotoId(photo?.id)
  );
}

export function getBackendPhotoId(photo) {
  if (!photo || isLocalUploadPhoto(photo)) return null;

  const candidate = photo.project_media_id || photo.id || photo.media_item_id || null;
  return isRealBackendPhotoId(candidate) ? candidate : null;
}

export function getBackendPhotoDeleteId(photo) {
  if (!photo || isLocalUploadPhoto(photo)) return null;

  const candidate = photo.media_item_id || photo.id || photo.project_media_id || null;
  return isRealBackendPhotoId(candidate) ? candidate : null;
}

export function getPhotoDisplayName(photo) {
  return (
    photo?.name ||
    photo?.filename ||
    photo?.original_filename ||
    photo?.original_name ||
    'Personal Photo'
  );
}

export function getPhotoImageUrl(photo) {
  return photo?.thumbnail_url || photo?.image_url || photo?.url || photo?.preview_url || null;
}

function createUploadItem(file) {
  const previewUrl = URL.createObjectURL(file);
  const randomPart =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    id: `local-${randomPart}`,
    file,
    name: file.name,
    filename: file.name,
    original_filename: file.name,
    mime_type: file.type,
    size_bytes: file.size,
    preview_url: previewUrl,
    thumbnail_url: previewUrl,
    image_url: previewUrl,
    url: previewUrl,
    status: 'uploading',
    error: null,
  };
}

function revokeUploadPreview(uploadItem) {
  if (uploadItem?.preview_url) {
    URL.revokeObjectURL(uploadItem.preview_url);
  }
}

function unwrapErrorDetail(errorOrResponse) {
  const raw = errorOrResponse?.response?.data || errorOrResponse?.data || errorOrResponse || {};
  return raw?.detail && typeof raw.detail === 'object' ? raw.detail : raw;
}

function getErrorMessage(errorOrResponse, fallback = 'Upload failed') {
  const detail = unwrapErrorDetail(errorOrResponse);
  return detail?.message || errorOrResponse?.message || fallback;
}

function isPhotoLimitBlock(errorOrResponse) {
  const detail = unwrapErrorDetail(errorOrResponse);
  const code = String(errorOrResponse?.code || detail?.code || '').toUpperCase();
  const source = String(detail?.source || '').toLowerCase();
  const message = getErrorMessage(errorOrResponse, '').toLowerCase();

  return (
    code === 'PHOTO_LIMIT_EXCEEDED' ||
    code === 'LIMIT_EXCEEDED' ||
    source === 'photos' ||
    message.includes('photo limit') ||
    message.includes('personal photo limit') ||
    message.includes('limit exceeded')
  );
}

function buildPhotoLimitResult(errorOrResponse, total) {
  const detail = unwrapErrorDetail(errorOrResponse);
  const current = Number(detail.current);
  const limit = Number(detail.limit);
  const remaining = Number(detail.remaining);

  return {
    ok: false,
    blocked: true,
    reason: 'photo_limit',
    total,
    uploaded: 0,
    failed: 0,
    current: Number.isFinite(current) ? current : null,
    limit: Number.isFinite(limit) ? limit : null,
    remainingSlots: Number.isFinite(remaining)
      ? remaining
      : Number.isFinite(limit) && Number.isFinite(current)
        ? Math.max(limit - current, 0)
        : 0,
    message: getErrorMessage(errorOrResponse, 'You have reached the personal photo limit.'),
    upgrade: detail.upgrade || null,
    available_offers: detail.upgrade?.available_offers ?? detail.available_offers,
    detail,
  };
}

function normalizePhotoList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.photos)) return response.photos;
  if (Array.isArray(response?.items)) return response.items;
  return [];
}

export default function useParentPhotos(projectId, { onBackendPhotosChange } = {}) {
  const [backendPhotos, setBackendPhotos] = useState([]);
  const [uploadItems, setUploadItems] = useState([]);

  const projectIdRef = useRef(projectId);
  const uploadItemsRef = useRef([]);

  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  useEffect(() => {
    uploadItemsRef.current = uploadItems;
  }, [uploadItems]);

  useEffect(() => {
    setBackendPhotos([]);
    setUploadItems((prev) => {
      prev.forEach(revokeUploadPreview);
      return [];
    });
  }, [projectId]);

  useEffect(() => {
    return () => {
      uploadItemsRef.current.forEach(revokeUploadPreview);
    };
  }, []);

  const setBackendPhotosFromServer = useCallback(
    (photosFromServer = []) => {
      const nextPhotos = Array.isArray(photosFromServer) ? photosFromServer : [];
      setBackendPhotos(nextPhotos);
      onBackendPhotosChange?.(nextPhotos);
    },
    [onBackendPhotosChange]
  );

  const refreshPhotos = useCallback(async () => {
    if (!projectId) {
      return { ok: false, error: new Error('projectId is required') };
    }

    try {
      const updatedData = await api.getParentPhotos(projectId);

      if (projectIdRef.current !== projectId) {
        return { ok: false, stale: true };
      }

      const nextPhotos = normalizePhotoList(updatedData);
      setBackendPhotosFromServer(nextPhotos);

      return { ok: true, photos: nextPhotos };
    } catch (error) {
      console.error('Failed to refresh parent photos:', error);
      return { ok: false, error };
    }
  }, [projectId, setBackendPhotosFromServer]);

  const removeUploadItem = useCallback((uploadItemId) => {
    setUploadItems((prev) => {
      const removed = prev.find((item) => item.id === uploadItemId);
      revokeUploadPreview(removed);
      return prev.filter((item) => item.id !== uploadItemId);
    });
  }, []);

  const markUploadItem = useCallback((uploadItemId, updates) => {
    setUploadItems((prev) =>
      prev.map((item) => (item.id === uploadItemId ? { ...item, ...updates } : item))
    );
  }, []);

  const addPhotos = useCallback(
    async (input) => {
      if (!input || !projectId) {
        return { ok: false, total: 0, uploaded: 0, duplicates: 0, failed: 0 };
      }

      const files = Array.isArray(input)
        ? input
        : typeof FileList !== 'undefined' && input instanceof FileList
          ? Array.from(input)
          : [input];

      const validFiles = files.filter(Boolean);

      if (validFiles.length === 0) {
        return { ok: true, total: 0, uploaded: 0, duplicates: 0, failed: 0 };
      }

      const targetProjectId = projectId;

      let presignResponse;

      try {
        const fileData = await Promise.all(
          validFiles.map(async (file) => ({
            filename: file.name,
            mime_type: file.type,
            size_bytes: file.size,
            fingerprint: await generateFileFingerprint(file),
          }))
        );

        presignResponse = await api.presignParentPhotos(targetProjectId, fileData);

        if (presignResponse?.ok === false || presignResponse?.blocked) {
          if (isPhotoLimitBlock(presignResponse)) {
            return buildPhotoLimitResult(presignResponse, validFiles.length);
          }

          return {
            ok: false,
            total: validFiles.length,
            uploaded: 0,
            duplicates: 0,
            failed: validFiles.length,
            error: presignResponse,
            message: getErrorMessage(presignResponse, 'Upload was not accepted by the server.'),
          };
        }
      } catch (error) {
        console.error('Failed to start photo upload:', error);

        if (isPhotoLimitBlock(error)) {
          return buildPhotoLimitResult(error, validFiles.length);
        }

        return {
          ok: false,
          total: validFiles.length,
          uploaded: 0,
          duplicates: 0,
          failed: validFiles.length,
          error,
          message: getErrorMessage(error, 'Upload failed. Please try again.'),
        };
      }

      const presignedFiles = Array.isArray(presignResponse?.files)
        ? presignResponse.files
        : [];

      if (presignedFiles.length < validFiles.length) {
        return {
          ok: false,
          total: validFiles.length,
          uploaded: 0,
          duplicates: 0,
          failed: validFiles.length,
          message: 'Upload was not accepted by the server.',
        };
      }

      const uploadItemsForBatch = validFiles.map(createUploadItem);
      setUploadItems((prev) => [...prev, ...uploadItemsForBatch]);

      const uploadOne = async (file, uploadItem, presignedFile) => {
        try {
          const {
            upload_url,
            ingestion_job_id,
            skip_upload,
            existing_media_item_id,
          } = presignedFile || {};

          if (!skip_upload) {
            if (!upload_url || !ingestion_job_id) {
              throw new Error('Upload was not accepted by the server.');
            }

            await api.uploadToPresignedUrl(upload_url, file, file.type);
            await api.confirmUpload(ingestion_job_id, file.name);
          }

          if (projectIdRef.current === targetProjectId) {
            markUploadItem(uploadItem.id, { status: 'done', error: null });
          }

          return {
            ok: true,
            uploadItemId: uploadItem.id,
            skippedDuplicate: Boolean(skip_upload),
            existingMediaItemId: existing_media_item_id || null,
          };
        } catch (error) {
          console.error('Failed to upload photo:', error);

          if (projectIdRef.current === targetProjectId) {
            markUploadItem(uploadItem.id, {
              status: 'failed',
              error: getErrorMessage(error, 'Upload failed'),
            });
          }

          return { ok: false, uploadItemId: uploadItem.id, error };
        }
      };

      const results = [];

      for (let i = 0; i < validFiles.length; i += UPLOAD_CONCURRENCY) {
        const fileBatch = validFiles.slice(i, i + UPLOAD_CONCURRENCY);
        const itemBatch = uploadItemsForBatch.slice(i, i + UPLOAD_CONCURRENCY);
        const presignBatch = presignedFiles.slice(i, i + UPLOAD_CONCURRENCY);

        const batchResults = await Promise.all(
          fileBatch.map((file, index) =>
            uploadOne(file, itemBatch[index], presignBatch[index])
          )
        );

        results.push(...batchResults);
      }

      if (projectIdRef.current === targetProjectId) {
        const refreshResult = await refreshPhotos();

        if (refreshResult.ok) {
          setUploadItems((prev) => {
            const next = [];

            prev.forEach((item) => {
              if (item.status === 'done') {
                revokeUploadPreview(item);
              } else {
                next.push(item);
              }
            });

            return next;
          });
        }
      }

      const duplicates = results.filter((result) => result.ok && result.skippedDuplicate).length;
      const uploaded = results.filter((result) => result.ok && !result.skippedDuplicate).length;
      const failed = results.filter((result) => !result.ok).length;

      return {
        ok: failed === 0,
        total: results.length,
        uploaded,
        duplicates,
        failed,
        results,
      };
    },
    [markUploadItem, projectId, refreshPhotos]
  );

  const removePhoto = useCallback(
    async (photoId) => {
      if (!projectId || !isRealBackendPhotoId(photoId)) {
        return {
          ok: false,
          error: new Error('projectId and a backend photo id are required'),
        };
      }

      const previousPhotos = backendPhotos;

      setBackendPhotos((prev) =>
        prev.filter((photo) => {
          const backendId = getBackendPhotoId(photo);
          const deleteId = getBackendPhotoDeleteId(photo);

          return backendId !== photoId && deleteId !== photoId;
        })
      );

      try {
        await api.deleteParentPhoto(projectId, photoId);
        const refreshResult = await refreshPhotos();

        return refreshResult.ok ? { ok: true } : refreshResult;
      } catch (error) {
        console.error('Failed to delete parent photo:', error);
        setBackendPhotos(previousPhotos);
        onBackendPhotosChange?.(previousPhotos);

        return { ok: false, error };
      }
    },
    [backendPhotos, onBackendPhotosChange, projectId, refreshPhotos]
  );

  const displayPhotos = useMemo(
    () => [
      ...backendPhotos
        .map((photo) => {
          const id = getBackendPhotoId(photo);
          if (!id) return null;

          return {
            kind: 'backend',
            id,
            deleteId: getBackendPhotoDeleteId(photo),
            photo,
          };
        })
        .filter(Boolean),
      ...uploadItems.map((upload) => ({
        kind: 'upload',
        id: upload.id,
        upload,
      })),
    ],
    [backendPhotos, uploadItems]
  );

  return {
    backendPhotos,
    uploadItems,
    displayPhotos,
    setBackendPhotosFromServer,
    refreshPhotos,
    addPhotos,
    removePhoto,
    removeUploadItem,
  };
}